import { Move, RotateCw } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import {
    registrationModeState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';

interface RegistrationModeToolProps {
    isDisabled: boolean;
    /** Why the modes are unavailable, appended to their tooltips while disabled. */
    disabledReason?: string;
    onModeSelected?: () => void;
}

export default function RegistrationModeTool({
    isDisabled,
    disabledReason,
    onModeSelected,
}: RegistrationModeToolProps) {
    const intl = useIntl();
    const translationLabel = intl.formatMessage({ id: 'translation' });
    const rotationLabel = intl.formatMessage({ id: 'rotation' });
    const reason = isDisabled ? disabledReason : undefined;
    const [tool, setTool] = useRecoilState(selectedToolState);
    const [registrationMode, setRegistrationMode] = useRecoilState(
        registrationModeState
    );

    const handleModeSelect = (mode: RegistrationMode) => {
        setRegistrationMode((prev) => ({
            ...prev,
            mode,
        }));
        if (tool !== 'registration') {
            setTool('registration');
        }
        onModeSelected?.();
    };

    return (
        <>
            <ToolbarButton
                icon={<Move />}
                tooltip={buildToolTooltip(intl, translationLabel, reason)}
                ariaLabel={translationLabel}
                selected={
                    tool === 'registration' &&
                    registrationMode.mode === RegistrationMode.TRANSLATION
                }
                disabled={isDisabled}
                onClick={() => handleModeSelect(RegistrationMode.TRANSLATION)}
                data-testid="registration-translate-mode"
            />
            <ToolbarButton
                icon={<RotateCw />}
                tooltip={buildToolTooltip(intl, rotationLabel, reason)}
                ariaLabel={rotationLabel}
                selected={
                    tool === 'registration' &&
                    registrationMode.mode === RegistrationMode.ROTATION
                }
                disabled={isDisabled}
                onClick={() => handleModeSelect(RegistrationMode.ROTATION)}
                data-testid="registration-rotate-mode"
            />
        </>
    );
}
